import { OutputDesignation, PortfolioType } from '@ddt-web/config';
import { WebUserDataConstants } from '@ddt-web/domain';
import { AllAvailableFeatureFlags, FeatureFlagLoaderService } from '@ddt-web/feature-flag';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';

import { CellData } from '../models/cell-data';
import { ColumnConfig } from '../models/column-config';
import { CellRow } from '../models/interfaces/component.interface';
import { CellDataCondition, CellRowCondition, TabDataCondition, TabDataTransform } from '../models/interfaces/tab.interface';
import { TabData } from '../models/tab-data';
import { TableData } from '../models/table-data';
import { CellUtil, Expression } from './cell-util';

/**
 * Checks if the provided feature flag is enabled or not.
 */
export class IsFeatureFlagEnabled implements TabDataCondition {
	constructor(private readonly featureFlag: AllAvailableFeatureFlags) { }

	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		return FeatureFlagLoaderService.instance.isFeatureEnabled(this.featureFlag);
	}
}

export class ConditionalTransformWrapper implements TabDataTransform {
	constructor(private readonly condition: TabDataCondition, private readonly trueTransform: TabDataTransform, private readonly falseTransform: TabDataTransform) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (this.condition.validate(data, portfolioData)) {
			return this.trueTransform ? this.trueTransform.transform(data, portfolioData) : data;
		} else {
			return this.falseTransform ? this.falseTransform.transform(data, portfolioData) : data;
		}
	}
}

/**
 * cells in hypo-chart is [CellData, CellData], then formattedValue && (||) rowValue is number[][] of chart data
 * so we want to show hypo chart section when every portfolio has at least one number in cellData.
 */
export class HasNoHypoChartDataMissing implements TabDataCondition {
	constructor(private excludeHiddenCell: boolean = false) { }
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const cells = get(data, 'tableData[0].cells');
		return (
			cells &&
			cells.every(cellArray =>
				cellArray
					.filter(cellData => get(cellData, 'config.visible') || !this.excludeHiddenCell)
					.every(cellData => !isNil(cellData.formattedValue) || !isNil(cellData.rawValue))
			)
		);
	}
}

export class HasAllDataMissingInAPortfolio implements TabDataCondition {
	constructor(
		private excludeHiddenCell: boolean = false, private checkForNaN: boolean = false, private checkForDashValues: boolean = false
	) { }
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const cells = get(data, 'tableData[0].cells');
		return cells && cells.some(cellData =>
			cellData.filter(cellData =>
				get(cellData, 'config.visible')
				|| !this.excludeHiddenCell).every(data => !data.rawValue || (this.checkForNaN && isNaN(data.rawValue)) || (this.checkForDashValues && data.rawValue === '-'))
		);
	}
}

export class HasAllDataMissingInAllPortfolios implements TabDataCondition {
	/**
	 * Checks if there is data within all portfolios
	 *
	 * @param excludeHiddenCell true if you want to ignore any cells which are not visible, false otherwise
	 * @param checkForNaN check if values which are NaN, returns true when values are NaN
	 * @param ignoreColumnIds list of column IDs to ignore when checking for missing data
	 */
	constructor(
		private excludeHiddenCell: boolean = false, private checkForNaN: boolean = true, private ignoreColumnIds: string[] = [], private checkForDashValues: boolean = false
	) { }
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const cells = get(data, 'tableData[0].cells');
		return cells && cells.every(cellData =>
			cellData
				.filter(cellData => (get(cellData, 'config.visible') || !this.excludeHiddenCell) && !this.ignoreColumnIds.includes(get(cellData, 'config.id')))
				.every(data => !data.rawValue || (this.checkForNaN && isNaN(data.rawValue)) || (this.checkForDashValues && data.rawValue === '-'))
		);
	}
}

export class HasOnlySpecificCategoryValue implements TabDataCondition {
	constructor(private headerValue: any, private isTransposed: boolean = false) { }

	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const headers = this.isTransposed ? get(data, 'tableData[0].headersX', []) : get(data, 'tableData[0].headersY', []);
		const headerIndex = headers.findIndex(header => header.rawValue === this.headerValue);

		if (headerIndex > -1) {
			if (this.isTransposed) {
				const cells = data.tableData[0].cells ?? [];
				return cells.some(cellArray => cellArray.some((cellData, cellDataIndex) => cellDataIndex === headerIndex && cellData.rawValue === 100));
			} else {
				const cells = get(data, `tableData[0].cells[${headerIndex}]`, []);
				return cells.some(cellData => cellData.rawValue === 100);
			}
		}

		return false;
	}
}

export class HasNoEsgData implements TabDataCondition {
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const cells = get(data, 'tableData[0].cells');
		return !(cells && cells.every(row => row.some(data => data.rawValue)));
	}
}

export class HasSpecificOutputDesignation implements TabDataCondition {
	constructor(private readonly outputDesignation: string) { }
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		return portfolioData && !!portfolioData.find(data => get(data, 'field.outputDesignation.rawValue') === this.outputDesignation);
	}
}

export class HasSpecificPortfolioType implements TabDataCondition {
	constructor(private readonly portfolioType: string) { }
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		return portfolioData && !!portfolioData.find(data => get(data, 'field.portfolioType.rawValue') === this.portfolioType);
	}
}
export class HasAnyPortfolioType implements TabDataCondition {
	constructor(private readonly portfolioTypes: string[]) { }
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		return portfolioData && !!portfolioData.find(data => this.portfolioTypes.includes(get(data, 'field.portfolioType.rawValue')));
	}
}

export class IncludeYieldCondition implements TabDataCondition {
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const outputFlags = get(data, 'displayConfig.clientOutput.outputFlags');
		const includeYield = outputFlags && outputFlags.find(flag => flag.getName() === 'includeYield');
		return includeYield && includeYield.getValue();
	}
}

export class CommonPeriodLessThanYearsCondition implements TabDataCondition {
	private readonly MONTHS_IN_YEAR = 12;
	constructor(private readonly years: number) { }
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		let totalMonths = CellUtil.findByExpression(data, [new Expression('totalMonths', 'tableData[0].cells[0]', 'rawValue')]);
		totalMonths = totalMonths || 0;
		return totalMonths <= this.years * this.MONTHS_IN_YEAR;
	}
}

export class IsSpecificOutputDesignation implements CellRowCondition {
	constructor(private readonly key: string) { }
	public validate(data: CellRow): boolean {
		return this.key ? get(data, 'field.outputDesignation.rawValue') === this.key : true;
	}
}

export class IsBenchmark implements CellRowCondition {
	public validate(data: CellRow): boolean {
		return get(data, 'field.outputDesignation.rawValue') === OutputDesignation.BENCHMARK || get(data, 'field.isBenchmark.rawValue') === true;
	}
}

export class IsTaxExempt implements CellRowCondition {
	public validate(data: CellRow): boolean {
		return !!data?.field?.flags?.rawValue?.['isPortfolioTaxExempt'];
	}
}

export class IsTaxExemptTabCondition implements TabDataCondition {
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		return !!portfolioData.some(item => item?.field?.flags?.rawValue?.['isPortfolioTaxExempt']);
	}
}

export class IsMissingSpecificCellData implements TabDataCondition {
	constructor(private readonly key: string) { }
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const result = data.tableData[0].cells[0].find(element => get(element, 'config.label.key') === this.key);

		// this will account for extractors that return arrays like MultiStoreExtractor
		if (result?.rawValue && result.rawValue instanceof Array) {
			const isSomeValueInvalid = result.rawValue.some(val => !val || val === 'NaN');
			return isSomeValueInvalid;
		}
		if (result?.rawValue && result?.rawValue !== 'NaN') {
			return false;
		}

		return true;
	}
}

export class IsSpecificPortfolioType implements CellRowCondition {
	constructor(private readonly key: string) { }
	public validate(data: CellRow): boolean {
		return this.key ? get(data, 'field.portfolioType.rawValue') === this.key : true;
	}
}

export class IsSpecificColumnLabel implements CellDataCondition {
	constructor(private readonly keys: string | string[]) { }
	public validate(data: CellData): boolean {
		const labelKeys = this.keys instanceof Array ? this.keys : [this.keys];
		return labelKeys.some(key => get(data, 'config.label.key') === key);
	}
}

export class FilterPortfolioTransform implements TabDataTransform {
	constructor(private readonly condition: CellRowCondition) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (data && data.tableData && data.tableData[0] && data.tableData[0].cells && portfolioData && portfolioData.length) {
			const portfolioToRemove: { name: string | number | any[] | boolean; index: number }[] = [];
			const headerYIndexes = [];

			portfolioData.forEach((portfolio, index) => {
				if (this.condition.validate(portfolio)) {
					portfolioToRemove.push({ name: portfolio.field['name'].rawValue, index: index });
					headerYIndexes.push(data.tableData[0].headersY.findIndex(data => data.rawValue === portfolio.field['name'].rawValue));
				}
			});
			if (portfolioData.length === portfolioToRemove.length) {
				return null;
			}
			const filteredCells = headerYIndexes.length ? data.tableData[0].cells.filter((data, index) => !headerYIndexes.includes(index)) : data.tableData[0].cells;
			const headersY = headerYIndexes.length ? data.tableData[0].headersY.filter((data, index) => !headerYIndexes.includes(index)) : data.tableData[0].headersY;
			const portfolioDataList =
				data.tableData[0].portfolioData &&
				data.tableData[0].portfolioData.map((data, index) => (portfolioToRemove.filter(portfolio => portfolio.index === index).length ? null : data));
			return new TabData(
				data.config,
				[new TableData(data.tableData[0].headersX, headersY, filteredCells, portfolioDataList, data.tableData[0].firstColumnTitle)],
				data.displayConfig,
				data.error
			);
		} else {
			return data;
		}
	}
}
/**
 * Returns the filtered tab without any invalid TableData.
 * TableData validation is done based on the provided condition and the provided portfolioData.
 *
 * If any of the TableData object fails validation, it is removed from the tab.
 * If all the TableData objects pass the validation, the tab is returned as it is.
 *
 * @class FilterPortfolioPerTableTransform
 * @implements {TabDataTransform}
 */
export class FilterPortfolioPerTableTransform implements TabDataTransform {
	constructor(private readonly condition: CellRowCondition) { }

	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (data?.tableData?.length && portfolioData?.length) {
			const portfolioIndexToRemove = [];

			portfolioData.forEach((portfolio, index) => {
				if (this.condition.validate(portfolio)) {
					portfolioIndexToRemove.push(index);
				}
			});
			if (portfolioData.length === portfolioIndexToRemove.length) {
				return null;
			}
			const updatedTableData = [];
			data.tableData.forEach((tableData, index) => {
				if (!portfolioIndexToRemove.includes(index)) {
					updatedTableData.push(tableData);
				}
			});
			return new TabData(
				data.config,
				updatedTableData,
				data.displayConfig,
				data.error
			);
		} else {
			return data;
		}
	}
}

export class FilterColumnTransform implements TabDataTransform {
	constructor(private readonly condition: CellDataCondition) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (data && data.tableData && data.tableData[0] && data.tableData[0].cells && portfolioData && portfolioData.length) {
			const indexes = [];
			data.tableData[0].headersX.forEach((headersX, index) => {
				if (this.condition.validate(headersX)) {
					indexes.push(index);
				}
			});

			return createTabWithFilteredColumn(data, indexes);
		} else {
			return data;
		}
	}
}

export class ReplaceCellFormattedValue implements TabDataTransform {
	constructor(private readonly replacementValue: any, private readonly key: string) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		data.tableData[0].cells.forEach(cellRow => {
			cellRow.forEach(cell => {
				if (cell.config.label && cell.config.label.key === this.key) {
					cell.formattedValue = this.replacementValue;
				}
			});
		});
		return data;
	}
}

export class ReplaceCellsIfAllDataMissingInAPortfolio implements TabDataTransform {
	constructor(private readonly replacedValue: any, private excludeHiddenCell: boolean = false) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		data.tableData[0].cells.forEach(cell => {
			if (cell.filter(cellData => cellData?.config?.visible || !this.excludeHiddenCell).every(data => !data.rawValue)) {
				cell.forEach(data => {
					data.formattedValue = this.replacedValue;

					if (data.children) {
						data.children.forEach(cellData => (cellData.formattedValue = this.replacedValue));
					}
				});
			}
		});
		return data;
	}
}

export class ReplaceRowsWithConditionTransform implements TabDataTransform {
	constructor(private readonly portfolioCondition: CellRowCondition, private readonly columnCondition: CellDataCondition, private readonly replacedValue: any) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		const headers = get(data, 'tableData[0].headersX');
		if (headers && headers.length) {
			const indexes = [];
			portfolioData.forEach((data, index) => {
				if (this.portfolioCondition.validate(data)) {
					indexes.push(index);
				}
			});
			const dataPointIdx = headers.findIndex(header => this.columnCondition.validate(header));
			data.tableData.forEach((table, index) => {
				if (indexes.includes(index) && dataPointIdx > -1) {
					table.cells.forEach(cellRow => {
						cellRow[dataPointIdx].formattedValue = this.replacedValue;
					});
				}
			});
		}
		return data;
	}
}

export class ReplaceCellWithConditionTransform implements TabDataTransform {
	constructor(private readonly portfolioCondition: CellRowCondition, private readonly columnCondition: CellDataCondition, private readonly replacedValue: any) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		const transformFunction = (cell: CellData) => {
			cell.formattedValue = this.replacedValue;
			return cell;
		};
		return CellUtil.findAndTransformCells(data, portfolioData, this.portfolioCondition, this.columnCondition, transformFunction);
	}
}

/**
 * Given a portfolioCondition and a columnCondition, if they are satisfied then set the cell's visibility to false.
 */
export class HideCellWithConditionTransform implements TabDataTransform {
	constructor(private readonly portfolioCondition: CellRowCondition, private readonly columnCondition: CellDataCondition) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		const transformFunction = (cell: CellData) => {
			return CellData.create({
				rawValue: cell.rawValue,
				formattedValue: cell.formattedValue,
				config: ColumnConfig.create({ ...cell.config, visible: false }),
				attribute: cell.attribute,
				parent: cell.parent,
				children: cell.children
			});
		};
		return CellUtil.findAndTransformCells(data, portfolioData, this.portfolioCondition, this.columnCondition, transformFunction);
	}
}

export class StockStyleSortTransform implements TabDataTransform {
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (!data.error) {
			const index = data.tableData[0].headersX.findIndex(cellData => cellData.rawValue.toString().toLowerCase() === 'unknown');
			if (index > -1) { data = CellUtil.moveCellDataForTabData(data, index, data.tableData[0].headersX.length); }
		}
		return data;
	}
}

export class SortTransform implements TabDataTransform {
	constructor(public sortOrder: string[]) { }

	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (!data.error) {
			this.sortOrder.forEach((rawValue, i) => {
				const index = data.tableData[0].headersX.findIndex(cellData => String(cellData.rawValue).toLowerCase() === rawValue.toLowerCase());
				if (i !== index && index > -1) {
					data = CellUtil.moveCellDataForTabData(data, index, i);
				}
			});
		}
		return data;
	}
}

export class HideTabTransform implements TabDataTransform {
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		return null;
	}
}

export class FilterAssetClassesTransform implements TabDataTransform {
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		const tableCells = get(data, 'tableData[0].cells');
		const headersY = get(data, 'tableData[0].headersY');
		const tablePortfolioData = get(data, 'tableData[0].portfolioData');
		if (tableCells) {
			const indexes = getAssetClassIndexes(portfolioData);
			const filteredCells = tableCells.filter((cell, idx) => indexes.indexOf(idx) === -1);
			const filteredHeadersY = headersY.filter((cell, idx) => indexes.indexOf(idx) === -1);
			const filteredPortfolioData = tablePortfolioData.filter((cell, idx) => indexes.indexOf(idx) === -1);
			return new TabData(
				data.config,
				[new TableData(data.tableData[0].headersX, filteredHeadersY, filteredCells, filteredPortfolioData, data.tableData[0].firstColumnTitle)],
				data.displayConfig,
				data.error
			);
		} else {
			return data;
		}
	}
}

export class FilterCompareAgainstBenchmarkTransform implements TabDataTransform {
	constructor(private readonly defaultValue?: string) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (data && data.tableData && data.tableData[0] && data.tableData[0].cells) {
			const indexes = getBenchmarkIndexes(portfolioData);
			const filteredCells = data.tableData[0].cells.map((cell, index) =>
				cell.map(item => {
					if (indexes.includes(index) && item.config.id === 'benchmark') {
						item.formattedValue = this.defaultValue ? this.defaultValue : undefined;
					}
					return item;
				})
			);
			return new TabData(
				data.config,
				[new TableData(data.tableData[0].headersX, data.tableData[0].headersY, filteredCells, data.tableData[0].portfolioData, data.tableData[0].firstColumnTitle)],
				data.displayConfig,
				data.error
			);
		} else {
			return data;
		}
	}
}

export function getBenchmarkIndexes(portfolioData: CellRow[]): number[] {
	const indexes = [];
	if (portfolioData && portfolioData.length) {
		portfolioData.forEach((data, index) => {
			if ((data.field['outputDesignation'] && data.field['outputDesignation'].rawValue === OutputDesignation.BENCHMARK) || get(data, 'field.isBenchmark.rawValue') === true) {
				indexes.push(index);
			}
		});
	}
	return indexes;
}

export function getAssetClassIndexes(portfolioData: CellRow[]): number[] {
	return (portfolioData || []).reduce((arr, data, idx) => {
		const portfolioType = data.find(column => column.config.id === 'portfolioType');
		if (get(portfolioType, 'formattedValue', '').startsWith(WebUserDataConstants.SUBTYPE.ASSET_CLASS)) {
			arr.push(idx);
		}
		return arr;
	}, []);
}

export class IsSomeHoldingsFund implements TabDataCondition {
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const index = data.tableData[0].headersY ? data.tableData[0].headersY.findIndex(cell => cell.config && cell.config.id === 'assetType') : -1;
		const cells = get(data, `tableData[0].cells[${index}]`);
		return cells && cells.some(cellData => cellData.rawValue === 'Fund');
	}
}

export class HasSpecificAssetClass implements TabDataCondition {
	constructor(private readonly assetClass: string, private readonly isTransposed: boolean = false) { }

	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const headers = this.isTransposed ? get(data, 'tableData[0].headersX') : get(data, 'tableData[0].headersY');
		const index = headers?.findIndex(cell => cell.config && cell.config.id === 'assetClass');

		if (this.isTransposed) {
			const cells = data?.tableData[0]?.cells ?? [];
			return cells.some(cellArray => cellArray.some((cellData, cellDataIndex) => cellDataIndex === index && cellData.rawValue === this.assetClass));
		} else {
			const cells = get(data, `tableData[0].cells[${index}]`, []);
			return cells.some(cellData => cellData.rawValue === this.assetClass);
		}
	}
}

export class FilterHoldingsTransform implements TabDataTransform {
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (data && data.tableData && data.tableData[0] && data.tableData[0].cells) {
			const indexes = [];
			const assetTypeIndex = data.tableData[0].headersY ? data.tableData[0].headersY.findIndex(cell => cell.config && cell.config.id === 'assetType') : -1;
			data.tableData[0].cells[assetTypeIndex].forEach((data, index) => {
				if (data.rawValue !== 'Fund') {
					indexes.push(index);
				}
			});
			const filteredCells = data.tableData[0].cells.map(cells => cells.filter((cell, index) => !indexes.includes(index)));
			const filteredHeadersX = data.tableData[0].headersX.filter((data, index) => !indexes.includes(index));
			return new TabData(
				data.config,
				[new TableData(filteredHeadersX, data.tableData[0].headersY, filteredCells, data.tableData[0].portfolioData, data.tableData[0].firstColumnTitle)],
				data.displayConfig,
				data.error
			);
		} else {
			return data;
		}
	}
}

export class FilterSpecificAssetClassTransform implements TabDataTransform {
	constructor(private readonly assetClass: string) { }
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (get(data, 'tableData[0].cells')) {
			const indexes = [];

			const assetClassIndex = get(data, 'tableData[0].headersY') ? data.tableData[0].headersY.findIndex(cell => cell.config && cell.config.id === 'assetClass') : -1;
			get(data, `tableData[0].cells[${assetClassIndex}]`, []).forEach((data, index) => {
				if (data.rawValue !== this.assetClass) {
					indexes.push(index);
				}
			});

			const filteredCells = data.tableData[0].cells.map(cells => cells.filter((cell, index) => indexes.includes(index)));
			const filteredHeadersX = get(data, 'tableData[0].headersX', []).filter((data, index) => indexes.includes(index));
			return new TabData(
				data.config,
				[new TableData(filteredHeadersX, data.tableData[0].headersY, filteredCells, data.tableData[0].portfolioData, data.tableData[0].firstColumnTitle)],
				data.displayConfig,
				data.error
			);
		} else {
			return data;
		}
	}
}

export class HasBestBenchmarkIdInPortfolio implements TabDataCondition {
	/**
	 * Checks if the data has information about the best benchmark id value which is an attribute to check if we have a license for the fund or not
	 * @param {TabData} data tab data of the table being rendered
	 * @param {CellRow[]} portfolioData portfolio data consisting of as of date, portfolio name etc..
	 * @return {boolean} returns true when the data contains best benchmark id attribute
	 */
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const cells = get(data, 'tableData[0].cells');
		return cells && cells.every(cellData => cellData.filter(cellData => get(cellData, 'config.id') === 'bestBenchmarkId'));
	}
}

export class IsBestBenchmarkIdInvalid implements CellRowCondition {
	/**
	 * Checks if the cell row has valid best benchmark id value, in which case it means we have a license for the fund
	 * @param {CellRow} data list of cells of a fund
	 * @return {boolean} returns true when invalid
	 */
	public validate(data: CellRow): boolean {
		return get(data.filter(cellData => get(cellData, 'config.id') === 'bestBenchmarkId')[0], 'rawValue') === undefined;
	}
}

export class ReplaceRowsWithDataConditionTransform implements TabDataTransform {
	constructor(private readonly portfolioCondition: CellRowCondition, private readonly columnCondition: CellDataCondition, private readonly replacedValue: any) { }
	/**
	 * Checks if the data in cell rows match the portfolio condition and renders them with replacedvalue
	 * @param {TabData} data initial funds data
	 * @param {CellRow[]} portfolioData initial portfolio data
	 * @return {TabData} rendered funds data
	 */
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		const headers = get(data, 'tableData[0].headersX');
		const cells = get(data, 'tableData[0].cells');
		if (cells && cells.length) {
			const indexes = [];
			cells.forEach((cell, index) => {
				if (this.portfolioCondition.validate(cell)) {
					indexes.push(index);
				}
			});
			const dataPointIdx = headers.findIndex(header => this.columnCondition.validate(header));
			data.tableData[0].cells.forEach((cell, index) => {
				if (indexes.includes(index) && dataPointIdx > -1) {
					cell[dataPointIdx].formattedValue = this.replacedValue;
				}
			});
		}
		return data;
	}
}

/**
 * If target-column data is not present return the same data
 * If target-column data is present and condition is true-
 * replaces all the columns that match the target-column key with the target-value
 */
export class ReplaceCellValueConditionTransform implements TabDataTransform {

	constructor(
		private condition: CellDataCondition, private targetColumnKey: string, private targetColumnValue: string, private sourceColumnKey: string
	) { }

	public transform(data: TabData): TabData {
		const replacedHeaderXIndex = get(data, 'tableData[0]')?.headersX?.findIndex(headerX => headerX?.config?.label?.key === this.targetColumnKey);
		const sourceColumnIndex = get(data, 'tableData[0]')?.headersX?.findIndex(headerX => headerX?.config?.label?.key === this.sourceColumnKey);

		if (replacedHeaderXIndex >= 0 && this.targetColumnValue && sourceColumnIndex) {
			data?.tableData?.forEach(tableData => {
				tableData?.cells?.forEach(cellData => {
					if (get(cellData, `${replacedHeaderXIndex}`) && this.condition?.validate(get(cellData, `[${sourceColumnIndex}]`))) {
						cellData[replacedHeaderXIndex].formattedValue = this.targetColumnValue;
					}
				});
			});
		}
		return data;
	}
}

/**
 * If target-row data is not present return the same data
 * If target-row data is present and condition is true-
 * replaces all the rows that match the target-row key with the target-value
 */
export class ReplaceCellValueConditionTransposeTransform implements TabDataTransform {

	constructor(private condition: CellDataCondition, private targetRowKey: string, private targetRowValue: string, private sourceRowKey: string) { }

	public transform(data: TabData): TabData {
		const indices = [];
		const replacedHeaderXIndex = get(data, `tableData[0].headersY`)?.findIndex(headersY => headersY?.config?.label?.key === this.targetRowKey);
		const sourceRowIndex = get(data, `tableData[0].headersY`)?.findIndex(headerValue => headerValue?.config?.label?.key === this.sourceRowKey);

		if (replacedHeaderXIndex >= 0 && this.targetRowValue && this.condition) {
			data?.tableData?.forEach(tableData => {
				get(tableData, `cells[${sourceRowIndex}]`)?.forEach((cellData, index) => {
					if (cellData && this.condition?.validate(cellData)) {
						indices.push(index);
					}
				});
				get(tableData, `cells[${replacedHeaderXIndex}]`)?.forEach((cellData, index) => {
					if (cellData && indices.includes(index)) {
						cellData.formattedValue = this.targetRowValue;
					}
				});
			});
		}
		return data;
	}
}

/**
 * If the provided value matches the cell formatted value return false, else return true
 */
export class IsNotSpecificValue {
	constructor(private value: string) { }

	public validate(cellData: CellData): boolean {
		return this.value && cellData?.formattedValue && cellData?.formattedValue !== this.value;
	}
}

export class IsMissingSpecificPortfolioData implements CellRowCondition {
	constructor(private readonly fields: string[]) { }
	public validate(data: CellRow): boolean {
		return get(this.fields, 'length') ? this.fields.every(field => !get(data, `field.${field}.rawValue`)) : true;
	}
}

export class IsBenchmarkBlkModel implements TabDataCondition {

	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const indexes = getBenchmarkIndexes(portfolioData);
		if (!isEmpty(indexes)) {
			return get(portfolioData, `[${indexes[0]}].field.portfolioType.rawValue`) === PortfolioType.MODEL;
		}
		return false;
	}
}

export class ContainsAssetClass implements TabDataCondition {

	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		return (portfolioData || []).some(portfolio => portfolio.some(cell => cell.formattedValue.startsWith(WebUserDataConstants.SUBTYPE.ASSET_CLASS)));
	}
}

export class IsAnyPortfolioContainOnlyOneFundForSelectedAssetClass implements TabDataCondition {
	constructor(private selectedAssetClass: string) { }
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const holdingInfoIndex = (get(data, 'tableData[0].headersX', [])).findIndex((headerX: CellData) => headerX.rawValue === 'Holdings');
		return (get(data, 'tableData[0].cells', [])).some((portfolio: CellData[]) => {
			const holdingsInfo: Array<any> = get(portfolio, `${holdingInfoIndex}.rawValue`) || [];
			const fundCount = holdingsInfo.reduce((sum, holding) => {
				if (
					holding.assetClass === this.selectedAssetClass &&
					(holding.legalType === 'ASSET_LEGAL_TYPE_OPEN_END' || holding.legalType === 'ASSET_LEGAL_TYPE_CLOSED_END')
				) { return sum + 1; }
				else { return sum; }
			}, 0);
			const otherCountIncludingFund = holdingsInfo.reduce((sum, holding) => {
				if (holding.assetClass === this.selectedAssetClass) { return sum + 1; }
				else { return sum; }
			}, 0);
			return fundCount === 1 && otherCountIncludingFund === 1;
		});
	}
}

export class FilterEmptyOrZeroColumnTransform implements TabDataTransform {
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (get(data, 'tableData[0].cells') && get(portfolioData, 'length')) {
			const indexes = [];
			const portfoliosDataOnlyRowIndex: number[] = get(data, 'tableData[0].headersY', []).reduce((result: number[], header, index) => {
				if (!!header.rawValue) {
					result.push(index);
				}
				return result;
			}, []);// Filter out rows from MultiColumnGridExtractor that are meta data rows
			get(data, 'tableData[0].headersX', []).forEach((headerX, index) => {
				if (data.tableData[0].cells.filter((row, index) => portfoliosDataOnlyRowIndex.includes(index)).every(row => !row[index].rawValue)) {
					indexes.push(index);
				}
			});
			return createTabWithFilteredColumn(data, indexes);
		} else {
			return data;
		}
	}
}

export function createTabWithFilteredColumn(data: TabData, filteredIndexes: number[]): TabData {
	const newTableData = get(data, 'tableData', []).map(tableData => {
		const filteredCells = get(tableData, 'cells', []).map(cells => {
			return cells.filter((data, index) => !filteredIndexes.includes(index));
		});

		const headersX = get(tableData, 'headersX', []).filter((data, index) => !filteredIndexes.includes(index));
		return new TableData(headersX, get(tableData, 'headersY', []), filteredCells, get(tableData, 'portfolioData'), get(tableData, 'firstColumnTitle'));
	});

	return new TabData(
		data.config,
		newTableData,
		data.displayConfig,
		data.error
	);
}

export class HasBenchmarkInPortfolios implements TabDataCondition {
	public validate(data: TabData, portfolioData: CellRow[]): boolean {
		const hasBenchmarkValue = portfolioData.some(data => get(data, 'field.isBenchmark.rawValue') === true);
		const hasSpecificOutputDesignation = portfolioData.some(data => get(data, 'field.outputDesignation.rawValue') === OutputDesignation.BENCHMARK);
		const hasBenchmark = hasBenchmarkValue || hasSpecificOutputDesignation;

		return portfolioData && hasBenchmark;
	}
}

export class CalculateDataWithBenchmarkTransform implements TabDataTransform {
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		const benchmarkIndex = portfolioData.findIndex(portfolio => get(portfolio, 'field.isBenchmark.rawValue')
			|| (get(portfolio, 'field.outputDesignation.rawValue') === OutputDesignation.BENCHMARK));
		if (benchmarkIndex >= 0) {
			const newTableData = get(data, 'tableData', []).map(tableData => {
				const newCells = [];
				get(tableData, 'cells', []).forEach((cells, index) => {
					if (index !== benchmarkIndex) {
						const cellsData = cells.map((cell, cellIndex) => {
							const rawValue = get(cell, 'rawValue', 0) - get(tableData, `cells[${benchmarkIndex}][${cellIndex}].rawValue`, 0);
							const formattedValue = get(cell, 'config.labelFormatter') ? cell.config.labelFormatter.format(rawValue) : rawValue;

							return CellData.create({
								rawValue: rawValue,
								formattedValue: formattedValue,
								config: cell.config,
								attribute: cell.attribute,
								parent: cell.parent,
								children: cell.children
							});
						});
						newCells.push(cellsData);
					}
				});
				const newHeadersY = get(tableData, 'headersY', []).filter((headerY, index) => index !== benchmarkIndex);
				const newPortfolioData = get(tableData, 'portfolioData', []).filter((headerY, index) => index !== benchmarkIndex);
				return new TableData(get(tableData, 'headersX', []), newHeadersY, newCells, newPortfolioData, get(tableData, 'firstColumnTitle'));
			});

			return new TabData(data.config, newTableData, data.displayConfig, data.error);
		} else {
			return data;
		}
	}
}

export class FilterBenchmarkColumnTransform implements TabDataTransform {
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		if (data?.tableData?.[0]?.cells && portfolioData?.length) {
			const benchmarkIndices = getBenchmarkIndexes(portfolioData);
			if (portfolioData.length === benchmarkIndices.length) {
				return null;
			}
			const isNotBenchmarkData = (data, index) => !benchmarkIndices.includes(index);
			const filteredCells = data.tableData[0].cells.map(cell => cell.filter(isNotBenchmarkData));
			const headersX = data.tableData[0].headersX.filter(isNotBenchmarkData);
			const portfolioDataList = data.tableData[0].portfolioData?.filter(isNotBenchmarkData);
			return new TabData(
				data.config,
				[new TableData(headersX, data.tableData[0].headersY, filteredCells, portfolioDataList, data.tableData[0].firstColumnTitle)],
				data.displayConfig,
				data.error
			);
		} else {
			return data;
		}
	}
}

export class FilterTaxExemptDataTransform implements TabDataTransform {
	public transform(data: TabData, portfolioData: CellRow[]): TabData {
		const taxExemptPortfolioIndexes = portfolioData.reduce(
			(acc, item, i) => item?.field?.flags?.rawValue?.['isPortfolioTaxExempt'] ? [...acc, i] : acc,
			[]
		);
		data?.tableData?.[0]?.cells?.forEach((cellArray, index) => {
			if (taxExemptPortfolioIndexes.includes(index)) {
				data.tableData[0].cells[index] = [];
			}
		});
		if (data.tableData[0].cells.every(cellArray => !cellArray?.length)) {
			return null;
		}
		return data;
	}
}

export class HasSpecificRowWithCellValue implements CellRowCondition {
	/**
	 * Checks for specific column's raw values in CellRow, multiple values of a column can be passed
	 * 
	 * @param {TabData} data
	 * @return {*} {boolean}
	 * @memberof HasSpecificRowWithCellValue
	 */
	constructor(
		private readonly cellValues: string[],
		private readonly shouldUseFormattedValueForCheck: boolean = false
	) { }

	public validate(data: CellRow): boolean {
		return data.some(cellData => {
			const valueToCheck = this.shouldUseFormattedValueForCheck ? cellData?.formattedValue : cellData?.rawValue;
			return this.cellValues.includes(valueToCheck?.toString());
		});
	}
}
